/* eslint-disable react/display-name */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { connect } from 'react-redux';
import { getAutocompleteCtepIdApi, getProtocolAuoCompleteApi } from 'actions/packet';
import { ATCONSTANTS as ATC } from 'appconstants';
import { showErrorNotification } from 'helpers';
import LeftSection from './LeftSection';
import CenterSection from './CenterSection';
import RightSection from './RightSection';
import {
  fillValidMapValues,
  getSiteName,
  isProtocolNumberValid,
  getRowIndex
} from './utils';

const getNonDuplicateRows = (id1, prt1, indexingRows) => !indexingRows.some(
  ({ siteCtepId: id2, protocolNumber: prt2 }) => id1 === id2 && prt1 === prt2
);

const getResults = (rows, indexingRows, setHasDuplicateRecords) => rows.filter(({ siteCtepId: id1, protocolNumber: prt1 }) => {
  // duplicate record found return false
  const noDuplicateRows = getNonDuplicateRows(id1, prt1, indexingRows);
  // when there are duplicate records then duplicaterecords to true.
  if (!noDuplicateRows) {
    setHasDuplicateRecords(true);
  }
  return noDuplicateRows;
});

const duplicateRowCheck = (fnProps) => {
  const {
    rows, indexingRows, setHasDuplicateRecords, setRows, processIndexRows
  } = fnProps;
  if (rows.length > 0) {
    // check for duplicate records and filter only non-duplicate records
    const results = getResults(rows, indexingRows, setHasDuplicateRecords);
    setRows(results);
    processIndexRows(results);
  }
};

const prepareRowObjFn = (obj, rows) => {
  const index = getRowIndex(obj, rows);
  if (index === -1) {
    rows.push(obj);
  }
};

const filterRowsFn = (filterRowsProps) => {
  const {
    siteName, siteCtepId, protocolData, rows
  } = filterRowsProps;
  protocolData.forEach((prtNum) => {
    if (prtNum != null && prtNum.trim().length > 0 && siteName != null && isProtocolNumberValid(prtNum)) {
      const obj = {
        siteCtepId,
        siteName,
        protocolNumber: prtNum.trim()
      };
      prepareRowObjFn(obj, rows);
    }
  });
};

const addOnlyInstitutions = (fnProps) => {
  const {
    institutionalData, rows, setRows, setProtocolData, setInstitutionalData, setIsAddingRows
  } = fnProps;
  institutionalData.forEach((ctepId) => {
    const siteCtepId = ctepId.trim().toUpperCase();
    const siteName = getSiteName(siteCtepId);
    if (siteName != null) {
      const obj = {
        siteCtepId,
        siteName
      };
      const index = getRowIndex(obj, rows);
      if (index === -1) {
        rows.push(obj);
      }
    }
  });
  setRows([...rows]);
  setProtocolData(new Set());
  setInstitutionalData(new Set());
  setIsAddingRows(false);
};

const QuickIndexing = forwardRef((props, ref) => {
  const {
    packet = {},
    setIndexRows = () => { },
    context,
    getAutocompleteCtepIdApiAction,
    getProtocolAuoCompleteApiAction,
    onSaveQI = () => { },
    indexingRows = [],
    hideSaveButton = false,
    isInstitutionDisabled = false,
    isProtocolDisabled = false,
    signatoryCtepId = null
  } = props;
  const { packetId, object, id } = packet;
  const [rows, setRows] = useState([]);
  // Using Set so that there can't be duplicate values entered in the fields
  const [protocolData, setProtocolData] = useState(new Set());
  const [institutionalData, setInstitutionalData] = useState(new Set());
  const [isAddingRows, setIsAddingRows] = useState(false);
  const [hasDuplicateRecords, setHasDuplicateRecords] = useState(false);

  const processIndexRows = (rRows) => {
    setTimeout(() => {
      setIndexRows(
        rRows.map((r) => ({
          siteCtepId: r.siteCtepId,
          protocolNumber: r.protocolNumber,
          pktId: packetId,
          objType: object
        }))
      );
    }, 100);
  };

  useImperativeHandle(ref, () => ({
    getIndexingRecords: () => rows.map((r) => ({
      siteCtepId: r.siteCtepId,
      protocolNumber: r.protocolNumber,
      pktId: packetId,
      objType: object
    }))
  }));

  // After the institute and protool values are validate.
  // Map every institute to every protocol and add the rows to the Grid
  const insertRows = () => {
    institutionalData.forEach((ctepId) => {
      const siteCtepId = ctepId.trim().toUpperCase();
      const siteName = getSiteName(siteCtepId);
      const filterRowsProps = {
        siteName, siteCtepId, protocolData, rows
      };
      filterRowsFn(filterRowsProps);
    });
    setRows([...rows]);
  };

  const processAdd = () => {
    insertRows();
    const duplicateRowCheckProps = {
      rows, indexingRows, setHasDuplicateRecords, setRows, processIndexRows
    };
    duplicateRowCheck(duplicateRowCheckProps);
    setProtocolData(new Set());
    setInstitutionalData(new Set());
    setIsAddingRows(false);
  };

  const fullSubscriberFn = () => {
    const fillSubscriber = fillValidMapValues(
      context,
      institutionalData,
      protocolData,
      getAutocompleteCtepIdApiAction,
      getProtocolAuoCompleteApiAction
    );
    if (fillSubscriber == null) {
      processAdd();
    } else {
      setIsAddingRows(true);
      fillSubscriber.subscribe(processAdd, (err) => {
        showErrorNotification(err.message);
        setIsAddingRows(false);
      });
    }
  };

  // This funciton will validate the entered values of instution and protocal in the free text
  // and validate if it exists using fillSubscriber to parellel api call and add the validate values to Grid
  // and clear the free text
  const onAdd = () => {
    if (isProtocolDisabled) {
      addOnlyInstitutions({
        institutionalData, rows, setRows, setProtocolData, setInstitutionalData, setIsAddingRows
      });
      return;
    }
    if (protocolData.size > 0 && institutionalData.size > 0) {
      fullSubscriberFn();
    }
  };

  const onDeleteRow = (row) => {
    rows.splice(getRowIndex(row, rows), 1);
    setRows([...rows]);
    processIndexRows([...rows]);
  };

  const onClearAll = () => {
    setRows([]);
    setIndexRows([]);
    setHasDuplicateRecords(false);
  };

  return (
    <>
      <div className="row">
        <LeftSection
          context={context}
          protocolData={protocolData}
          institutionalData={institutionalData}
          setProtocolData={setProtocolData}
          setInstitutionalData={setInstitutionalData}
          getAutocompleteCtepIdApiAction={getAutocompleteCtepIdApiAction}
          getProtocolAuoCompleteApiAction={getProtocolAuoCompleteApiAction}
          isInstitutionDisabled={isInstitutionDisabled}
          isProtocolDisabled={isProtocolDisabled}
          signatoryCtepId={signatoryCtepId}
        />
        <CenterSection onAdd={onAdd} isAddingRows={isAddingRows} />
        <RightSection rows={rows} onDeleteRow={onDeleteRow} />
      </div>
      {hasDuplicateRecords && (
        <div className="color-orange-darker">
          Some of these are already indexed. Those records are not being staged.
        </div>
      )}
      {!hideSaveButton && (
        <div className="row">
          <div className="col align-right">
            <button
              data-id={`${ATC.PACKETID}${id}_CONFIRM${ATC.BUTTON}`}
              type="button"
              className="btn turquoise mr-2"
              disabled={rows.length === 0}
              onClick={onSaveQI}
            >
              Confirm
            </button>
            <button
              data-id={`${ATC.PACKETID}${id}_CLEAR_ALL${ATC.BUTTON}`}
              type="button"
              className="Modal-close btn close"
              onClick={onClearAll}
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </>
  );
});

const mapDispatchToProps = {
  getAutocompleteCtepIdApiAction: getAutocompleteCtepIdApi,
  getProtocolAuoCompleteApiAction: getProtocolAuoCompleteApi
};

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(QuickIndexing);
